// A practical SSG guide for exporting a Series and taking its RSS feed elsewhere. Keep the entire
// import graph Node-safe: this page describes app controls but never imports the authenticated app.
import { Link as RouterLink } from 'react-router';
import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AudiotrackIcon from '@mui/icons-material/AudiotrackOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import HeadphonesIcon from '@mui/icons-material/HeadphonesOutlined';
import PodcastsIcon from '@mui/icons-material/PodcastsOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';
import RssFeedIcon from '@mui/icons-material/RssFeedOutlined';
import { SplashChrome } from './splash-chrome';
import { Band, CtaBand, FeatureCard, SectionBody, SectionHeader } from './splash-sections';
import { metaDescription, ORIGIN, splashMeta } from './series-ld';
import type { Route } from './+types/publishing';

const TITLE = 'How to Publish an AI Podcast and Export Your Series | Gnothi';
const DESCRIPTION = metaDescription(
  'Read and listen in Gnothi, export chapters or an EPUB, follow your RSS feed in a podcast app, and submit it to major directories.',
);
const PATH = '/publishing';
const external = { target: '_blank', rel: 'noopener noreferrer' } as const;

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    'script:ld+json': {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: TITLE,
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}`,
    },
  },
];

function Steps({ children }: { children: ReactNode }) {
  return <Box component="ol" sx={{ maxWidth: 720, mx: 'auto', mb: 2, pl: 3 }}>{children}</Box>;
}

function Step({ number, children }: { number: number; children: ReactNode }) {
  return <Typography component="li" sx={{ mb: 1 }} value={number}>{children}</Typography>;
}

function Hero() {
  return (
    <Band color="dark" hero>
      <Stack spacing={2} sx={{ textAlign: 'center', alignItems: 'center' }}>
        <PodcastsIcon sx={{ fontSize: 52 }} />
        <Typography variant="overline" component="p" sx={{ color: 'splash.accentOnDark', fontWeight: 800 }}>From a finished chapter to a feed</Typography>
        <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }, maxWidth: 820 }}>
          Read it here. Share the feed. Publish when you're ready.
        </Typography>
        <Typography component="p" sx={{ maxWidth: 720, opacity: 0.86 }}>
          Every Series has a built-in reader and player. This guide covers written exports, listening by RSS, public directories, and reusable audio segments.
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'center', flexWrap: 'wrap' }}>
          {([['Listen', '#listen'], ['Downloads', '#downloads'], ['Podcast apps', '#subscribe'], ['Public show', '#directories'], ['Rolls', '#rolls']] as const).map(([label, href]) => (
            <Button key={href} component="a" href={href} color="inherit" size="small">{label}</Button>
          ))}
        </Stack>
        <Button component="a" href="#listen" variant="contained" size="large">Start with the simple option</Button>
      </Stack>
    </Band>
  );
}

function Listen() {
  return (
    <Band id="listen">
      <SectionHeader icon={<HeadphonesIcon />} tone="prompt" title="Read and listen in Gnothi" deck="This is the default. There is nothing to publish or configure first." />
      <SectionBody center>
        Open a completed chapter in the app or site to read it and use the built-in player. Narration is included with each generated chapter. Your Series can remain private inside Gnothi while you do this.
      </SectionBody>
    </Band>
  );
}

function Downloads() {
  return (
    <Band id="downloads" color="grey">
      <SectionHeader icon={<DownloadIcon />} tone="organization" title="Download the writing" deck="Use the chapter actions menu for one chapter, or export the completed book." />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}><FeatureCard title="Export chapter" tone="organization">Downloads a .txt file with the chapter heading and Markdown-formatted text. You can open it as plain text or use the Markdown in another editor.</FeatureCard></Grid>
        <Grid size={{ xs: 12, md: 4 }}><FeatureCard title="Export book" tone="organization">Builds an EPUB from the Series' completed chapters in chronological order. The download link is temporary; the export does not require a public feed.</FeatureCard></Grid>
        <Grid size={{ xs: 12, md: 4 }}><FeatureCard title="M4A audiobook" tone="organization"><strong>Coming soon.</strong> A finished-book M4A download is not available yet. Read or listen in Gnothi, or use the RSS options below today.</FeatureCard></Grid>
      </Grid>
    </Band>
  );
}

function Feed() {
  return (
    <Band id="feed">
      <SectionHeader icon={<RssFeedIcon />} tone="themes" title="Enable a feed for podcast apps" deck="A feed gives you one audio RSS URL to copy into a podcast app or share with friends." />
      <Steps>
        <Step number={1}>Open the Series, choose Manage, then Publishing.</Step>
        <Step number={2}>Choose Feed, read and accept the publishing-rights and public-exposure acknowledgment, then save.</Step>
        <Step number={3}>Open Subscribe and copy the Audio RSS address.</Step>
      </Steps>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Alert severity="info">
          A Feed URL stays out of public directories until you submit it, but it has no password or access controls. Anyone who receives it can open or forward it. Switching the Series back to Private cannot recall copies already downloaded elsewhere, so do not submit a friends-only feed to a public directory.
        </Alert>
      </Box>
    </Band>
  );
}

function Subscribe() {
  return (
    <Band id="subscribe" color="grey">
      <SectionHeader icon={<HeadphonesIcon />} tone="themes" title="Follow the feed on iPhone or Android" deck="Copy the Audio RSS address from Gnothi first. These direct subscriptions do not list the show in a public catalog." />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FeatureCard title="Apple Podcasts on iPhone or iPad" tone="themes" footer={<Link href="https://support.apple.com/guide/iphone/iph19bb8e705/ios" {...external}>Official Apple instructions ↗</Link>}>
            Open Podcasts and tap Library. Tap More (•••), choose Follow a Show by URL, paste the feed address, and tap Follow.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FeatureCard title="AntennaPod on Android" tone="themes" footer={<Stack spacing={0.5}><Link href="https://antennapod.org/" {...external}>Get AntennaPod ↗</Link><Link href="https://antennapod.org/documentation/getting-started/subscribe" {...external}>Official AntennaPod instructions ↗</Link></Stack>}>
            We recommend AntennaPod for a simple open-source option. Open the lower-right ••• menu, tap + Add Podcast, choose the RSS address option, paste the feed address, preview it, and subscribe.
          </FeatureCard>
        </Grid>
      </Grid>
    </Band>
  );
}

function Directories() {
  return (
    <Band id="directories">
      <SectionHeader icon={<PublicIcon />} tone="prompt" title="Submit a public show" deck="Enable the Gnothi feed first, then submit that same audio RSS address to each service separately. Gnothi does not list it for you." />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="YouTube and YouTube Music" tone="prompt" footer={<Stack spacing={0.5}><Link href="https://studio.youtube.com/" {...external}>Open YouTube Studio ↗</Link><Link href="https://support.google.com/youtube/answer/13973017" {...external}>Official RSS steps ↗</Link></Stack>}>
            Choose Create, New podcast, then Submit RSS feed. Verify using the email in the feed, choose which episodes to import, wait for ingestion, review them under Content → Podcasts, and publish. YouTube makes static-image videos from the artwork and audio; availability and account requirements vary.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Spotify" tone="prompt" footer={<Stack spacing={0.5}><Link href="https://creators.spotify.com/" {...external}>Open Spotify for Creators ↗</Link><Link href="https://support.spotify.com/creators/article/adding-a-new-show-to-your-spotify-for-creators-account/" {...external}>Official show steps ↗</Link></Stack>}>
            Add a new show, choose Find an existing show, then Somewhere else. Enter the RSS URL and the verification code sent to the feed's email address. This keeps Gnothi as the host.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FeatureCard title="Apple Podcasts" tone="prompt" footer={<Stack spacing={0.5}><Link href="https://podcastsconnect.apple.com/my-podcasts" {...external}>Open Apple Podcasts Connect ↗</Link><Link href="https://podcasters.apple.com/support/897-submit-a-show" {...external}>Official submission steps ↗</Link></Stack>}>
            Add a New Show with an RSS feed. Review the imported details, set content rights and availability, save, resolve validation warnings, then publish for Apple's review.
          </FeatureCard>
        </Grid>
      </Grid>
      <SectionBody center>
        Directory approval and feed updates take their own processing time. Public destination links you later add in Gnothi are shortcuts for listeners; adding one does not submit the show.
      </SectionBody>
    </Band>
  );
}

function Rolls() {
  return (
    <Band id="rolls" color="dark">
      <SectionHeader icon={<AudiotrackIcon />} tone="themes" title="Add an intro, mid-roll, or outro" deck="Upload your own reusable audio under Manage → Audio → Rolls." />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><FeatureCard title="First-chapter intro" tone="themes">A one-time opening for the beginning of the Series.</FeatureCard></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><FeatureCard title="Intro" tone="themes">A reusable opening for ordinary episodes.</FeatureCard></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><FeatureCard title="Mid-roll" tone="themes">Placed around a topic change and a suitable pause, using the timing you choose.</FeatureCard></Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}><FeatureCard title="Outro" tone="themes">A reusable close for credits, announcements, or a call to action.</FeatureCard></Grid>
      </Grid>
      <SectionBody center>
        Upload sponsor messages, announcements, credits, and calls to action as reusable clips. Existing rendered episodes keep their old audio until you explicitly re-render them. Before submitting to YouTube, review or remove advertising roll segments: YouTube restricts embedded advertiser-supplied ad breaks, while host-read sponsorships follow separate branded-content rules.{' '}
        <Link href="https://support.google.com/youtube/answer/13525207" {...external} sx={{ color: 'splash.accentOnDark' }}>Read YouTube's RSS guidance ↗</Link>
      </SectionBody>
    </Band>
  );
}

export default function Publishing() {
  return (
    <SplashChrome active="publishing">
      <Hero />
      <Listen />
      <Downloads />
      <Feed />
      <Subscribe />
      <Directories />
      <Rolls />
      <CtaBand color="light" title="Make the next chapter" deck="Create the Series first. Its reader, narration, exports, and publishing controls stay together." label="Explore Series" to="/series" />
    </SplashChrome>
  );
}
